/**
 * Who decides, and whose stream it is (13-22, D-13-L-2, D-13-L-15). Data only: the rules that read it are in
 * `lib/recordAuthority.ts`.
 *
 * People appear here as UPNs only, never as display names. This is the one file in the dashboard that names staff
 * UPNs: the authority needs a UPN to compare with the signed-in account, and an App Role cannot say "this one
 * Oversight member".
 *
 * Sources:
 * - `SAH_REPORTABILITY_DECIDERS`: D-13-L-2. The UPN is the one confirmed live in the `Whistleblower Disclosures
 *   Oversight` group read (12.1-13.2: "users 2"), the same address 12.1-13's `restrict_list.py` grants.
 * - `SAH_DECIDER_DELEGATES`, `WORKSAFE_NOTICE_DELEGATES`: D-13-L-2's veto, taken by the system owner on 13 Oct 2026
 *   (D-13-33): the delegates were named in chat that day, in order, and each address was confirmed the same day as an
 *   enabled member account (a Graph read as the admin account). Elsewhere they are described by role only.
 * - `STREAM_CARE_MANAGERS`: mirrors `CM_TAGS` in `Harmony-QMS/build/incident-notify/c3_resolve_new.js`
 *   (D-12.1-15.2-2), which keys the C3 card's Care Managers by display name. Each display name is resolved to the
 *   UPN recorded for that person, with role `CareManager`, in `Harmony-QMS/build/action-register/practice_rows_v131.py`
 *   `PEOPLE` (13.1-08: the practice cards owned by each CareManager App Role holder, site user ids resolved on
 *   5 Oct 2026). No address is derived from a name pattern (one does not follow it). Re-check against the
 *   CareManager App Role assignments (`appRoleAssignedTo`) before the ship (13-30).
 */

import { liveChoice } from './live';

/**
 * The SaH reportability and SIRS-notice decider(s) (D-13-L-2): the only people who may record a reportability
 * decision on an aged-care incident, or a SIRS notice. Lower-cased; compared without case or surrounding spaces.
 *
 * The delegates who step in while the decider is away are `SAH_DECIDER_DELEGATES` (D-13-L-2's veto, taken 13 Oct 2026).
 */
export const SAH_REPORTABILITY_DECIDERS: readonly string[] = ['batra@harmonyhomecare.com.au'];

/** One person in a chain of deciders: the UPN (lower-cased) and the role words the panels show (never a name). */
export interface DelegateEntry {
  readonly upn: string;
  readonly role: string;
}

/** The SaH decider's role words, first in every "who decides" line. */
export const SAH_DECIDER_ROLE = 'the SaH reportability decider';

/**
 * The SaH decider's delegates, **in order** (D-13-L-2's veto, taken 13 Oct 2026, D-13-33): if the decider is away,
 * the first steps in, then the second. Any listed person may record an aged-care reportability decision or a SIRS
 * notice; the order is what the panels say, so everyone knows who is next.
 */
export const SAH_DECIDER_DELEGATES: readonly DelegateEntry[] = [
  { upn: 'andy@harmonyhomecare.com.au', role: 'the system owner' },
  { upn: 'amran@harmonyhomecare.com.au', role: 'the People & Culture lead' },
];

/** The WorkSafe notifier's role words: the HR & WHS App Role (the People & Culture lead holds it today). */
export const WORKSAFE_NOTIFIER_ROLE = 'HR & WHS';

/**
 * The WorkSafe notice's named delegates, **in order**, beside the HR & WHS App Role (D-13-L-2's veto, taken 13 Oct
 * 2026, D-13-33). The notifier is gated by the App Role, not a UPN, so the delegate is named here and no App Role
 * assignment changes (that would be a tenant action). A delegate may record the WorkSafe notice only; the WHS verb
 * stays HR & WHS's.
 */
export const WORKSAFE_NOTICE_DELEGATES: readonly DelegateEntry[] = [
  { upn: 'hamza@harmonyhomecare.com.au', role: 'the named WorkSafe delegate' },
];

/**
 * The streams whose reportability is the SaH decider's (D-13-L-2): Support at Home, and Brokered or Private (aged-care
 * brokerage). **D-13-22-4, ruled 13 Oct 2026 (D-13-33):** also both mixed units, `Both NDIS & Aged Care` and
 * `All Stake holders (…)`, because an aged-care client may be involved. Verbatim from `liveColumns.json`.
 */
export const AGED_CARE_STREAMS: readonly string[] = liveChoice(
  'incidents',
  'BusinessUnit',
  'Support at Home Package',
  'Brokered or Private',
  'Both NDIS & Aged Care',
  'All Stake holders (Clients: NDIS, Aged Care & Staff)',
);

const [SAH_STREAM, NDIS_STREAM, BOTH_STREAMS, ALL_STAKEHOLDERS, STAFF] = liveChoice(
  'incidents',
  'BusinessUnit',
  'Support at Home Package',
  'NDIS',
  'Both NDIS & Aged Care',
  'All Stake holders (Clients: NDIS, Aged Care & Staff)',
  'Staff',
);

/** C3's `SAH_CMS`, as UPNs, in C3's order. */
const SAH_CARE_MANAGERS: readonly string[] = [
  'sandeep@harmonyhomecare.com.au',
  'kate.p@harmonyhomecare.com.au',
  'dennika@harmonyhomecare.com.au',
];

/** C3's `NDIS_CMS`, as UPNs, in C3's order. */
const NDIS_CARE_MANAGERS: readonly string[] = [
  'julieth@harmonyhomecare.com.au',
  'shobha@harmonyhomecare.com.au',
];

/**
 * `BusinessUnit` → its Care Managers' UPNs (C3's `CM_TAGS`). It marks "your stream" on a record and is **never a
 * gate** (D-13-L-15: any CareManager may act on any Incident, Complaint or Feedback). A unit not in the map (for
 * example `Brokered or Private`, which C3 cards to the CMs' manager instead) has no stream Care Managers.
 */
export const STREAM_CARE_MANAGERS: Readonly<Partial<Record<string, readonly string[]>>> = {
  [SAH_STREAM]: SAH_CARE_MANAGERS,
  [NDIS_STREAM]: NDIS_CARE_MANAGERS,
  [BOTH_STREAMS]: [...SAH_CARE_MANAGERS, ...NDIS_CARE_MANAGERS],
  [ALL_STAKEHOLDERS]: [...SAH_CARE_MANAGERS, ...NDIS_CARE_MANAGERS],
  [STAFF]: [],
};
